import type { TrackingObject } from '@pdcplan/shared/api/Tracking';
import { z } from 'zod';
import { SHIFT_TYPE_SUGGESTIONS_MAX } from '../constants';
import { ApplicationTypeEnum, UserLoginTypeEnum } from '@pdcplan/shared/enums';
import { LanguageCode } from '@pdcplan/shared/generated/language/TeamPlan';

/**
 * Keys used to access local storage
 * Keys are arbitrary, values are not
 */
export enum LocalStorageKey {
  token = 'token',
  tokenTimestamp = 'tokenTimestamp',
  userId = 'userId',
  userType = 'userType',
  fullName = 'fullName',
  currentTeam = 'currentTeam',
  userTracking = 'userTracking',
  currentLanguage = 'currentLanguage',
  key = 'key',
  shiftTypeSuggestions = 'shiftTypeSuggestions',
}

const trackingObjectSchema: z.ZodType<TrackingObject> = z.object({
  userHash: z.string(),
  sessionStart: z.coerce.date(),
  viewPort: z.object({ width: z.number(), height: z.number() }),
  device: z.string(),
  version: z.string(),
  applicationType: z.enum(ApplicationTypeEnum),
  hasBiometricAuth: z.boolean(),
  hasCalendarLink: z.boolean(),
  hasCalendarImport: z.boolean(),
  hasPreferencePattern: z.boolean(),
  navigations: z.array(
    z.object({
      viewName: z.string(),
      timeStamp: z.coerce.date(),
      activities: z.array(
        z.object({
          name: z.string(),
          timeStamp: z.coerce.date(),
          auxDate: z.coerce.date().nullable(),
        })
      ),
    })
  ),
});

export const localStorageSchemas = {
  [LocalStorageKey.token]: z.string(),
  [LocalStorageKey.tokenTimestamp]: z.number(),
  [LocalStorageKey.userId]: z.number(),
  [LocalStorageKey.userType]: z.enum(UserLoginTypeEnum),
  [LocalStorageKey.fullName]: z.string(),
  [LocalStorageKey.currentTeam]: z.string(),
  [LocalStorageKey.currentLanguage]: z.enum(LanguageCode),
  [LocalStorageKey.key]: z.string(),
  [LocalStorageKey.shiftTypeSuggestions]: z.record(
    z.string(),
    z.array(z.string()).max(SHIFT_TYPE_SUGGESTIONS_MAX)
  ),
  [LocalStorageKey.userTracking]: trackingObjectSchema,
} as const satisfies Record<LocalStorageKey, z.ZodType>;

export type LocalStorageItemType<T extends LocalStorageKey> = z.infer<
  (typeof localStorageSchemas)[T]
>;
